/**
 * inboxRowActions — PURE per-row action-visibility rules for the Unified
 * Inbox list (M-UX-5.2, `architecture-ux-restructure.md` §6.5 "Einheitliche
 * Aktionen je Zeile").
 *
 * "Öffnen" dispatches to an existing detail component by `sourceCollection`
 * (`ReceivedInvoiceDetail` / `InboundDocumentDetail` / `ReceiptScanModal` —
 * wired in `UnifiedInboxPage`, not here). For `scanned_receipts` there is NO
 * detail view beyond the booking-confirmation modal (`ReceiptScanModal`,
 * reused 1:1 from `ReceiptScanPage` — it is not a general detail viewer and
 * pre-fills booking fields), so "Öffnen" for a scan row is only meaningful
 * while the row is still bookable (`status === 'new'`, i.e. the underlying
 * `scanned_receipts.status === 'extracted'` with no linked expense yet) —
 * matching `ReceiptScanPage`'s own `scan.status === 'extracted'` gate on its
 * "Buchen"-CTA. Terminal/queued scan states (`processing`/`failed`/`discarded`)
 * have no detail affordance at all in the existing component surface.
 *
 * "Als Ausgabe verbuchen" (`ExpenseBookingModal` for upload/email,
 * `ReceiptScanModal` for scan — same dispatch as "Öffnen" for that channel)
 * is gated fail-closed on `EXPENSES_ENABLED && isPremium && !isReadOnly`
 * (`canBookExpense`, passed in by the caller) AND on the row itself not
 * already being linked to an expense (`status === 'new'`).
 *
 * "Ausgabe ansehen" (deep-link to `/dashboard/ausgaben`, §6.5) appears once a
 * row is `status === 'booked'` — the pipeline's terminal, successful state.
 *
 * Advisor read-only (§6.5): for `scanned_receipts` the ONLY existing viewer
 * IS the booking-confirmation modal (`ReceiptScanModal` — there is no
 * separate read-only detail component in the current codebase). "Öffnen" for
 * a scan row is therefore additionally gated on `!isReadOnly` — unlike
 * received/inbound, where `ReceivedInvoiceDetail`/`InboundDocumentDetail` are
 * pure viewers safe to open in the Mandanten-Modus.
 *
 * @module components/inbox/inboxRowActions
 */

import type { UnifiedInboxItem } from '../../utils/unifiedInbox/types';

/** Caller-supplied context that is NOT derivable from the row itself. */
export interface RowActionContext {
  /** `EXPENSES_ENABLED && isPremium && !isReadOnly` (§6.5 Buchen-Gate). */
  canBookExpense: boolean;
  /** `useIsReadOnly()` — `true` only in the Mandanten-Modus (§6.5 Advisor read-only). */
  isReadOnly: boolean;
}

/** Which actions a given row should offer, resolved fail-closed. */
export interface RowActionCapabilities {
  /** Show an "Öffnen" affordance for this row. */
  canOpen: boolean;
  /** Show an "Als Ausgabe verbuchen" affordance for this row. */
  canBook: boolean;
  /** Show an "Ausgabe ansehen" deep-link (`/dashboard/ausgaben`) for this row. */
  canViewExpense: boolean;
  /**
   * Show a "Verwerfen" (Soft-Discard) affordance for this row (M-V4-D2,
   * architecture-ux-v4.md §4.3). `danger`-variant menu item. Never offered for
   * E-Mail rows (`inbound_documents` is client-read-only / WORM), never for the
   * Advisor (read-only), and only while the row is not yet booked/terminal.
   */
  canDiscard: boolean;
}

/**
 * Resolves the action capabilities of one {@link UnifiedInboxItem}. Pure —
 * never throws, never mutates its input.
 *
 * @param item the row to resolve actions for.
 * @param ctx booking-eligibility context (§6.5 Buchen-Gate).
 * @returns the row's action capabilities.
 */
export function resolveRowActions(item: UnifiedInboxItem, ctx: RowActionContext): RowActionCapabilities {
  const isScan = item.sourceCollection === 'scanned_receipts';
  const isEmail = item.sourceCollection === 'inbound_documents';
  const isBookable = item.status === 'new';

  // M-V4-D2 (§4.3): `canDiscard = !isReadOnly && status ∈ {new,failed,draft}
  // && sourceCollection !== 'inbound_documents'`. `processing` (in-flight) and
  // `booked`/`discarded` are never discardable; the E-Mail channel is WORM
  // (client-read-only), so it never offers Verwerfen in v4.
  const isDiscardableStatus =
    item.status === 'new' || item.status === 'failed' || item.status === 'draft';

  return {
    canOpen: isScan ? isBookable && !ctx.isReadOnly : true,
    canBook: ctx.canBookExpense && isBookable,
    canViewExpense: item.status === 'booked' && item.linkedExpenseId !== null,
    canDiscard: !ctx.isReadOnly && isDiscardableStatus && !isEmail,
  };
}
